import { isAxiosError } from 'axios'

/**
 * Extrai uma mensagem legível do erro retornado pela API.
 *
 * Prioridade:
 * 1. `error.response.data.message` (formato padrão do error-handler do backend
 *    senne: `{ status: 'error', message: '...' }`).
 * 2. `error.response.data.error` (formatos legados que ainda existem).
 * 3. `error.message` (mensagem nativa do Error, ex.: "Network Error").
 * 4. Fallback genérico.
 *
 * Uso típico em React Query:
 *
 *     {mutation.isError && (
 *         <ErrorBox>{getApiErrorMessage(mutation.error)}</ErrorBox>
 *     )}
 */
export function getApiErrorMessage(
    err: unknown,
    fallback = 'Ocorreu um erro inesperado. Tente novamente.'
): string {
    if (isAxiosError(err)) {
        const data = err.response?.data as
            | { message?: unknown; error?: unknown }
            | undefined

        const message =
            typeof data?.message === 'string' && data.message.trim().length > 0
                ? data.message
                : typeof data?.error === 'string' && data.error.trim().length > 0
                ? data.error
                : null

        if (message) return message
    }

    if (err instanceof Error && err.message) return err.message

    return fallback
}

/**
 * Status HTTP do erro, se disponível. Útil para discriminar 404 (sessão
 * expirada), 409 (conflito), 413 (payload grande), etc., além da mensagem.
 */
export function getApiErrorStatus(err: unknown): number | null {
    if (isAxiosError(err)) {
        return err.response?.status ?? null
    }
    return null
}
